/**
 * Iter55 - bounded PDF download helper.
 *
 * The estimate and invoice PDF endpoints now (Iter55) render WeasyPrint in
 * a bounded thread executor and can respond with:
 *   - 200 application/pdf  - bytes ready to save.
 *   - 504 application/json - render timeout (>20s).
 *   - 500 application/json - render failure (WeasyPrint threw).
 *   - 401 - invalid auth query token.
 *
 * A raw anchor link leaks the JSON/HTML error page as the tab content,
 * which is a poor experience. This helper fetches the URL, inspects the
 * response, and only triggers a browser download on 200. All non-200
 * outcomes return a structured object the caller renders as a toast.
 */
export async function downloadPdf(url, filename) {
  let resp;
  try {
    resp = await fetch(url, { credentials: "same-origin" });
  } catch (e) {
    return { ok: false, kind: "network", message: "Network error while downloading PDF. Please try again." };
  }
  if (resp.status === 200) {
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    return { ok: true, kind: "success" };
  }
  if (resp.status === 504) {
    return { ok: false, kind: "timeout",
             message: "PDF is taking too long to render - please try again in a moment." };
  }
  if (resp.status === 500) {
    return { ok: false, kind: "render_error",
             message: "PDF render failed. If this keeps happening, contact support." };
  }
  if (resp.status === 401) {
    return { ok: false, kind: "auth",
             message: "Session expired - please sign in again and retry the PDF download." };
  }
  if (resp.status === 404) {
    return { ok: false, kind: "not_found",
             message: "This document no longer exists." };
  }
  return { ok: false, kind: "unknown", message: `PDF download failed (HTTP ${resp.status}).` };
}
